import React, { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { tv } from 'tailwind-variants'
import Tooltip from 'components/tooltip'
import Input from 'components/input'
import {
  Visible,
  Filter,
  Colour,
  Sort,
  Group,
  Lock,
  Asterisk,
  Expand,
  Save,
  Search,
  Freeze,
  Cross,
  SmallRowHeight,
  MediumRowHeight,
  TallRowHeight,
  ExtraTallRowHeight,
  Chart,
  MoveColumn,
  Permissions,
  Join,
  History,
  Link as LinkIcon,
  Options,
  ViewActions,
  ChevronDown,
  Grid,
  ImportData,
  ExportData,
  PencilSparkles
} from 'components/icons'
import {
  CustomViewPanelMode,
  toggleCustomViewPanel,
  useCustomViewPanel
} from 'components/spreadsheet/components/custom-view/panel'
import { rowHeights, IViewPortColumn } from 'components/spreadsheet/types'
import ViewMenu, { VIEW_LIST_MAX_WIDTH, VIEW_LIST_MIN_WIDTH } from 'components/spreadsheet/components/menu/views/view'
import FilterMenu from 'components/spreadsheet/components/menu/views/filter'
import ColourMenu from 'components/spreadsheet/components/menu/views/colour'
import LockMenu from 'components/spreadsheet/components/menu/views/lock'
import SortMenu from 'components/spreadsheet/components/menu/views/sort'
import GroupMenu from 'components/spreadsheet/components/menu/views/group'
import HideMenu from 'components/spreadsheet/components/menu/views/hide'
import RequiredMenu from 'components/spreadsheet/components/menu/views/required'
import Spinner from 'components/spinner'
import { canEditWithAi, useEditWithAi } from 'components/spreadsheet/components/custom-view/useEditWithAi'
import CalendarMenu from 'components/spreadsheet/components/menu/views/calendar'
import ChartMenu from 'components/spreadsheet/components/menu/views/chart'
import PivotMenu from 'components/spreadsheet/components/menu/views/pivot'
import MatrixMenu from 'components/spreadsheet/components/menu/views/matrix'
import FreezeMenu from 'components/spreadsheet/components/menu/views/freeze'
import ColumnOrderMenu from 'components/spreadsheet/components/menu/views/order'
import RowHeightMenu from 'components/spreadsheet/components/menu/views/rowheight'
import SaveMenu from 'components/spreadsheet/components/menu/views/save'
import SearchMenu from 'components/spreadsheet/components/menu/views/search'
import SettingsMenu from 'components/spreadsheet/components/menu/views/settings'
import ImportMenu from 'components/spreadsheet/components/menu/views/import'
import ExportMenu from 'components/spreadsheet/components/menu/views/export'
import AuditModal from 'components/spreadsheet/components/modal/views/audit'
import PermissionsModal from 'components/spreadsheet/components/modal/views/permissions'
import Duplicate from 'components/duplicate'
import {
  CommentDisplayTypes,
  ValidationDisplayTypes,
  viewSupportsGrouping,
  ViewTypes
} from 'components/spreadsheet/constants/const'
import { getViewCapabilities, isCapabilityEnabled } from 'components/spreadsheet/constants/viewCapabilities'
import { INITIAL_CONTEXT_MENU_STATE, IntegrationProviders, IntegrationSyncResourceTypes } from 'app-constants'
import { IContextMenuState, ITableViewColumn, ITableViewWithColumns } from 'types'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { PANEL_WIDTH, panelAnchor } from 'components/menu/panel'
import {
  buildSlicerRuntimeFilters,
  getColumnsPerUserRole,
  getOriginalColumns,
  getOriginalData,
  getOriginalViewColumns,
  isColumnLockedByRules
} from 'components/spreadsheet/helpers/functions'
import { useSlicers } from 'providers/slicers'
import { getTreeLeafColumnId } from 'components/spreadsheet/helpers/grouping'
import { useApplicationStore } from 'hooks/application'
import { TOOLBAR_HEIGHT_CLASS, TOOLBAR_MENU_OFFSET } from 'style/constants'
import {
  deleteTable,
  deleteView,
  saveCurrentView,
  updateTable,
  updateView
} from 'components/spreadsheet/contexts/data/actions'
import { hydrateView, HydrateViewResult } from 'components/spreadsheet/helpers/hydrateView'
import { subscribeOpenViewSettingsMenu } from 'components/spreadsheet/helpers/view-settings-bus'
import { useProject } from 'hooks/useProject'
import { useAuth } from 'hooks/auth'
import { useInlineRename } from 'components/spreadsheet/hooks/useInlineRename'
import usePersonalSaveBlocked, { getSaveBlockedTooltip } from 'components/spreadsheet/hooks/usePersonalSaveBlocked'
import { hasUnsavedChanges } from 'components/spreadsheet/helpers/customViewLatest'
import DeleteResource from 'components/delete'
import ArchiveExplanation from 'components/delete/ArchiveExplanation'
import TableLookupsDialog, {
  OPEN_LOOKUPS_EVENT,
  OpenLookupsDetail
} from 'components/spreadsheet/components/modal/views/tablejoins'
import AppendFileModal from 'components/spreadsheet/components/modal/views/append'
import IntegrationModal from 'components/spreadsheet/components/modal/views/integration'
import Related from 'components/related'
import { Link, useNavigate, generatePath } from 'react-router-dom'
import DuplicateChildren from 'components/duplicateChildren'
import { PERMISSIONS } from 'helpers/auth'
import { TABLE } from 'routes'
import BackupModal from '../modal/views/backups'
import HistoryMenu from '../menu/views/history'
import ViewConfigMenu from '../menu/views/viewconfig'
import { ToolbarSkeleton } from 'components/spreadsheet/components/loadingSkeleton'
import PageHeader from 'components/pageHeader'
import ViewTabs from './viewTabs'
import { PAGE_HEADER_ACTIONS, PAGE_HEADER_GROUP } from 'components/pageHeader/styles'
import { canImportData } from 'components/spreadsheet/helpers/importData'

const toolbar = tv({
  slots: {
    element:
      'truncate rounded m-[5px] p-[5px] min-w-[25px] font-semibold flex items-center border border-transparent justify-start',
    button:
      // Not clipped, so a count can sit on an icon button's corner; the view name adds its own truncate.
      'whitespace-nowrap rounded m-[5px] p-[5px] min-w-[25px] font-semibold flex items-center border border-transparent justify-start cursor-pointer transition-[background-color] duration-[85ms] ease-in hover:bg-surface-sunken',
    pulse:
      'overflow-hidden text-ellipsis whitespace-nowrap m=[5px] p-[5px] min-w-[25px] font-semibold flex items-center border border-transparent justify-start cursor-pointer bg-light-red rounded-full animate-pulse-pink transform-[scale(1)]'
  },
  variants: {
    state: {
      // A toggle that's on (filters applied, rows grouped, …): the document
      // header's Done blue, fill and icon, so it's plain to see. toolbar-active
      // marks it for the view row, whose resting icons are grey.
      active: {
        button: 'toolbar-active bg-really-light-blue text-blue hover:bg-light-blue'
      },
      error: {
        button: 'bg-light-red transition-[border] hover:border-divider hover:bg-light-red'
      },
      disabled: {
        button: 'opacity-20 cursor-default'
      }
    }
  }
})

const { element, button, pulse } = toolbar()

// The table page's view row groups, by what they do, in the row's order. Export
// and save come last.
const VIEW_ROW_GROUPS: { id: string; label: string }[] = [
  { id: 'view-settings', label: 'View settings' },
  { id: 'bring-in', label: 'Bring in data' },
  { id: 'find', label: 'Find and arrange' },
  { id: 'layout', label: 'Layout' },
  { id: 'rules', label: 'Rules' },
  { id: 'export-save', label: 'Export and save' }
]
// The same groups in the order they fold away when the row is
// short of room: least used first. Export and save never fold.
const FOLD_ORDER = ['rules', 'bring-in', 'layout', 'view-settings', 'find']
// Each group's entries in the View actions menu, which lists them all, labelled,
// so an action whose icon you don't recognise is still there by name, Save
// included, although its group never folds away.
const GROUP_MENU_ITEMS: Record<string, string[]> = {
  'view-settings': ['chart', 'pivot', 'matrix'],
  'bring-in': ['import', 'joins'],
  find: ['search', 'filter', 'sort', 'group', 'required'],
  layout: ['freeze', 'hide', 'rowHeight', 'columnOrder'],
  rules: ['colour', 'lock'],
  'export-save': ['export', 'save']
}
const GROUP_GAP = 16 // PAGE_HEADER_ACTIONS' gap-4
const MORE_BUTTON_WIDTH = 28
// The view's actions keep the same room whichever view is open, so the tabs
// beside them do too: the room a spreadsheet view's actions need, the most any
// view has, as fitViewRow counts it, so none of them fold. Its groups' buttons
// (Bring in data, Find and arrange, Layout, Rules, Export and save), each a 28px
// square 2px from the next, each group with a 16px gap, View actions, and the
// 16px fitViewRow keeps clear before them: 534px.
const ACTION_BUTTON_WIDTH = 28
const ACTION_BUTTON_GAP = 2 // PAGE_HEADER_GROUP's gap-0.5
const SPREADSHEET_GROUP_SIZES = [2, 4, 4, 2, 2]
const VIEW_ACTIONS_WIDTH =
  GROUP_GAP +
  SPREADSHEET_GROUP_SIZES.reduce(
    (sum, size) => sum + size * ACTION_BUTTON_WIDTH + (size - 1) * ACTION_BUTTON_GAP + GROUP_GAP,
    0
  ) +
  MORE_BUTTON_WIDTH

// A group of the page layout's related controls, tight together. Controls that
// don't apply are false, so a group with none renders nothing (and leaves no
// gap). Each control carries the toolbar's own 5px margin; inside a group the
// group spaces them instead.
const ToolbarGroup: React.FC<{ label: string; id?: string; children: React.ReactNode }> = ({ label, id, children }) => {
  if (React.Children.toArray(children).length === 0) return null
  return (
    <div
      className={`${PAGE_HEADER_GROUP} [&_[data-testid$='-button']]:m-0`}
      role="group"
      aria-label={label}
      data-group-id={id}
    >
      {children}
    </div>
  )
}

interface ToolbarProps {
  uniqueNumber: number
  fullScreen: boolean
  setFullScreen: (fullScreen: boolean) => void
  handleToggleCollapseAll: () => void
  columns: IViewPortColumn[]
}

type IntegrationState = {
  open: boolean
  provider?: IntegrationProviders
  syncType?: IntegrationSyncResourceTypes
  name?: string
}

const Toolbar: React.FC<ToolbarProps> = ({
  uniqueNumber,
  fullScreen,
  setFullScreen,
  handleToggleCollapseAll,
  columns
}) => {
  const {
    spreadsheetData,
    setSpreadsheetData,
    filterMenu,
    setFilterMenu,
    colourMenu,
    setColourMenu,
    groupMenu,
    setGroupMenu,
    inProcess,
    originalViewId,
    resetSpreadsheetClick
  } = useDataContext()
  const { project, projectAdmin } = useProject()
  const { sidebarWidth } = useApplicationStore()
  const { selections: slicerSelections } = useSlicers()
  const navigate = useNavigate()

  const capabilities = getViewCapabilities(spreadsheetData.viewDetails.type).toolbar
  const customViewPanel = useCustomViewPanel()
  const { copying: copyingForAi, ask: askToEditWithAi, dialog: editWithAiDialog } = useEditWithAi()

  // All toolbar pop-out menus share one state map keyed by menu id, so adding
  // a new menu is a single union-member change instead of another `useState`
  // call. ``setMenuStateFor(id)`` returns a stable-shape setter that menu
  // components can pass straight to their ``setMenuState`` prop; calling it
  // with ``{open: false}`` (or ``INITIAL_CONTEXT_MENU_STATE``) removes the
  // entry from the map so a closed menu costs nothing.
  type MenuId =
    | 'view'
    | 'sort'
    | 'hide'
    | 'lock'
    | 'required'
    | 'columnOrder'
    | 'freeze'
    | 'rowHeight'
    | 'save'
    | 'search'
    | 'import'
    | 'export'
    | 'settings'
    | 'chart'
    | 'calendar'
    | 'history'
    | 'pivot'
    | 'matrix'
    | 'viewConfig'
  const [openMenus, setOpenMenus] = useState<Partial<Record<MenuId, IContextMenuState>>>({})
  const menuStateOf = (id: MenuId): IContextMenuState => openMenus[id] ?? INITIAL_CONTEXT_MENU_STATE
  const setMenuState = (id: MenuId, state: IContextMenuState) => {
    setOpenMenus((prev) => {
      if (!state.open) {
        if (prev[id] === undefined) return prev
        const next = { ...prev }
        next[id] = void 0
        return next
      }
      return { ...prev, [id]: state }
    })
  }
  // Curried form for passing as ``setMenuState`` prop to child Menu components.
  const setMenuStateFor = (id: MenuId) => (state: IContextMenuState) => setMenuState(id, state)

  const [auditModal, setAuditModal] = useState<boolean>(false)
  const [deleteTableModal, setDeleteTableModal] = useState<boolean>(false)
  const [joinsModal, setJoinsModal] = useState<boolean>(false)
  // The lookup to show first when the lookups open from a column's header.
  const [joinsFocus, setJoinsFocus] = useState<string | undefined>(undefined)
  useEffect(() => {
    const openFromHeader = (event: Event) => {
      const detail = (event as CustomEvent<OpenLookupsDetail>).detail
      if (detail?.tableId !== spreadsheetData.tableDetails.publicId) return
      setJoinsFocus(detail.joinId)
      setJoinsModal(true)
    }
    window.addEventListener(OPEN_LOOKUPS_EVENT, openFromHeader)
    return () => window.removeEventListener(OPEN_LOOKUPS_EVENT, openFromHeader)
  }, [spreadsheetData.tableDetails.publicId])
  const [relatedModal, setRelatedModal] = useState<boolean>(false)
  const [viewDuplicatesModal, setViewDuplicatesModal] = useState<boolean>(false)
  const [backupModal, setBackupModal] = useState<boolean>(false)
  const [appendFileModal, setAppendFileModal] = useState<boolean>(false)
  const [importFromTableModal, setImportFromTableModal] = useState<boolean>(false)
  const [duplicateTableModal, setDuplicateTableModal] = useState<boolean>(false)
  const [deleteViewModal, setDeleteViewModal] = useState<boolean>(false)
  const [viewToDelete, setViewToDelete] = useState<{ id: string; name: string } | null>(null)
  const [permissionsModal, setPermissionsModal] = useState<boolean>(false)

  const [integration, setIntegration] = useState<IntegrationState>({
    open: false
  })

  // Whether the buttons fold into the View Configuration menu. Measured, not
  // guessed from the window: they fold only when the row holding them actually
  // overflows, and come back once it's as wide as they needed. (A fixed "window
  // minus sidebar under 800px" rule folded them with room to spare, and ignored
  // how wide the toolbar itself was, as in a narrow embedded table.)
  const [isCompact, setIsCompact] = useState(false)
  const toolbarRowRef = useRef<HTMLDivElement>(null)
  const widthNeeded = useRef<number>(0)

  const openIntegration = (provider: IntegrationProviders, syncType: IntegrationSyncResourceTypes, name: string) =>
    setIntegration({ open: true, provider, syncType, name })

  const closeIntegration = () => setIntegration({ open: false })

  const checkOverflow = () => {
    const row = toolbarRowRef.current
    // The table page folds its view row a group at a time instead (below).
    if (!row || !inProcess) return
    if (!isCompact) {
      if (row.scrollWidth > row.clientWidth + 1) {
        widthNeeded.current = row.scrollWidth
        setIsCompact(true)
      }
    } else if (row.clientWidth >= widthNeeded.current) {
      setIsCompact(false)
    }
  }

  // On the table page the view row folds a group at a time, least used first,
  // leaving them to its View actions menu (which lists every action anyway).
  // Each group's width is measured while it shows (and remembered while it's
  // folded), and the actions keep as many as fit in their fixed room
  // (VIEW_ACTIONS_WIDTH, or half the row if that's less); the views have the rest.
  const [foldedGroups, setFoldedGroups] = useState<string[]>([])
  const groupWidths = useRef<Record<string, number>>({})

  const fitViewRow = () => {
    const row = toolbarRowRef.current
    if (!row || inProcess) return
    // The groups that have something in them for this view, folded or not.
    const presentGroups = (row.dataset.groups ?? '').split(' ').filter(Boolean)
    row.querySelectorAll<HTMLElement>('[data-group-id]').forEach((group) => {
      groupWidths.current[group.dataset.groupId as string] = group.offsetWidth
    })
    // Not laid out (or no layout at all, as in tests): fold nothing.
    if (row.clientWidth === 0) return
    const { paddingLeft, paddingRight } = getComputedStyle(row)
    const room = Math.min(
      VIEW_ACTIONS_WIDTH,
      (row.clientWidth - (parseFloat(paddingLeft) || 0) - (parseFloat(paddingRight) || 0)) / 2
    )
    const available = room - GROUP_GAP
    // The groups, and the View actions button that always follows them.
    const widthOf = (groups: string[]) =>
      groups.reduce((sum, id) => sum + (groupWidths.current[id] ?? 0) + GROUP_GAP, 0) + MORE_BUTTON_WIDTH
    const shownAfter = (folded: string[]) => presentGroups.filter((group) => !folded.includes(group))
    const fits = (folded: string[]) => widthOf(shownAfter(folded)) <= available
    const folded: string[] = []
    for (const id of FOLD_ORDER) {
      if (fits(folded)) break
      if (presentGroups.includes(id)) folded.push(id)
    }
    if (folded.join() !== foldedGroups.join()) setFoldedGroups(folded)
  }

  useLayoutEffect(() => {
    checkOverflow()
    fitViewRow()
  })
  useEffect(() => {
    const row = toolbarRowRef.current
    if (!row || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => {
      checkOverflow()
      fitViewRow()
    })
    observer.observe(row)
    return () => observer.disconnect()
  })

  // A different view needs its own measurement, so start it unfolded.
  useEffect(() => {
    setIsCompact(false)
    setFoldedGroups([])
  }, [spreadsheetData.viewDetails.publicId])

  const allColumns = getColumnsPerUserRole(spreadsheetData)

  // For transpose views, hide/lock operates on original columns (transposed rows), not synthetic columns
  const hideAndLockColumns = getOriginalViewColumns(spreadsheetData)

  // The columns to show in the hide dropdown
  const visibleHideColumns = getOriginalColumns(spreadsheetData).filter(
    (column) => !(column as ITableViewColumn).required || (column as ITableViewColumn).isJoined
  )

  // The columns to show in the required dropdown. Lock state lives in row-lock
  // rules now, not the retired column.locked boolean.
  const visibleRequiredColumns = hideAndLockColumns.filter(
    (column) =>
      !spreadsheetData.userConfiguration.hiddenColumns.includes(column.publicId) &&
      !isColumnLockedByRules(spreadsheetData.userConfiguration.rowLockSettings, column.publicId) &&
      !column.isJoined
  )

  const noOfColours = spreadsheetData.userConfiguration.colourSettings.length
  const noOfSorts = spreadsheetData.userConfiguration.sortSettings.length
  const noOfFilters = spreadsheetData.userConfiguration.filterSettings.length
  // Document slicers filter this table from outside the view's own settings — surface them on the
  // filter button so readers scrolled away from the slicer know the rows are constrained.
  const noOfSlicerFilters = buildSlicerRuntimeFilters(spreadsheetData.viewDetails.columns, slicerSelections).length
  const noOfHidden = spreadsheetData.userConfiguration.hiddenColumns.length
  const noOfLocked = spreadsheetData.userConfiguration.rowLockSettings.length
  const noOfRequired = visibleRequiredColumns.filter(
    (column: ITableViewColumn) =>
      column.required && !spreadsheetData.userConfiguration.hiddenColumns.includes(column.publicId)
  ).length
  const noOfGroups = spreadsheetData.userConfiguration.groupSettings.length
  const chartSettings = spreadsheetData.userConfiguration.chartSettings
  const calendarSettings =
    spreadsheetData.userConfiguration.calendarSettings ?? spreadsheetData.viewDetails.calendarSettings
  const isCalendarConfigured =
    calendarSettings !== null && !!calendarSettings.labelColumnPublicId && !!calendarSettings.startColumnPublicId
  const isLoading = spreadsheetData.loading

  // Which of the view's actions are on, by their key in the View actions menu: the toolbar lights
  // their buttons, and the menu its rows, so a folded group still shows what's on.
  const activeActions: Record<string, boolean> = {
    chart: spreadsheetData.viewDetails.type === ViewTypes.CALENDAR ? isCalendarConfigured : chartSettings !== null,
    pivot: !!spreadsheetData.viewDetails.transposeSettings?.pivotColumnId,
    matrix: !!spreadsheetData.viewDetails.matrixSettings?.pivotColumnId,
    search: spreadsheetData.searchTerm !== '',
    filter:
      noOfFilters > 0 ||
      noOfSlicerFilters > 0 ||
      spreadsheetData.viewDetails.displayValidationErrorRows !== ValidationDisplayTypes.SHOW_VALIDATION ||
      spreadsheetData.viewDetails.displayCommentRows !== CommentDisplayTypes.SHOW_ALL,
    sort: noOfSorts > 0,
    group: noOfGroups > 0,
    hide: noOfHidden > 0,
    import: !!spreadsheetData.tableDetails.isSynced,
    joins: spreadsheetData.tableDetails.joins.length > 0,
    colour: noOfColours > 0,
    lock: noOfLocked > 0,
    required: noOfRequired > 0,
    freeze: spreadsheetData.viewDetails.frozenIndex > 0
  }
  // How many rules a filter, sort, group and so on holds, as a bubble on its button's icon: the
  // button's blue says it's on, the bubble how much.
  const metadataFilters =
    (spreadsheetData.viewDetails.displayValidationErrorRows !== ValidationDisplayTypes.SHOW_VALIDATION ? 1 : 0) +
    (spreadsheetData.viewDetails.displayCommentRows !== CommentDisplayTypes.SHOW_ALL ? 1 : 0)
  const withCount = (icon: React.ReactNode, count: number) =>
    count > 0 ? (
      <span className="relative flex">
        {icon}
        <span
          aria-hidden
          className="pointer-events-none absolute -top-2 -right-2.5 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-blue px-[3px] text-[9px] leading-none font-semibold text-white tabular-nums ring-2 ring-surface"
        >
          {count}
        </span>
      </span>
    ) : (
      icon
    )

  // Something on in a group folded into View actions, which then carries a dot.
  const foldedActive = foldedGroups.some((id) => (GROUP_MENU_ITEMS[id] ?? []).some((key) => activeActions[key]))

  // The view's own menu (This view) is as wide as the New view menu, so the two read as one family
  // and a submenu fits beside it; the list of views is sized to its names (VIEW_LIST_MIN_WIDTH to VIEW_LIST_MAX_WIDTH).
  const viewWidth = 260
  // All view actions: a plain menu, as wide as the view's own menu.
  const viewConfigWidth = 260
  const filterWidth = PANEL_WIDTH.lg
  const colourWidth = PANEL_WIDTH.lg
  const sortWidth = PANEL_WIDTH.md
  const groupWidth = PANEL_WIDTH.md
  const hideWidth = PANEL_WIDTH.sm
  const lockWidth = PANEL_WIDTH.lg
  const requiredWidth = PANEL_WIDTH.sm
  const columnOrderWidth = PANEL_WIDTH.sm
  const freezeWidth = PANEL_WIDTH.sm
  const rowHeightWidth = PANEL_WIDTH.sm
  const saveWidth = 200
  const searchWidth = 550
  const importWidth = 420
  // A plain menu rather than a panel: a few short choices, as wide as the longest needs.
  const exportWidth = 200
  const settingsWidth = 280
  const chartWidth = 350
  const historyWidth = 350

  const offset = TOOLBAR_MENU_OFFSET

  // A panel opened from the view actions menu takes that menu's place: its right edge on the table
  // page, where the menu hangs from the right, and its left edge when embedded.
  const fromViewConfig = (panelWidth: number) => {
    const { top, left } = menuStateOf('viewConfig')
    return { top, left: inProcess ? left : `${parseFloat(left) + viewConfigWidth - panelWidth}px` }
  }

  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()
  const { user } = useAuth()

  // Inline rename of the current view name via double-click on the view selector.
  // Mirrors the view menu's canEditView: the default view is never renameable, admins
  // can rename any view, and non-admins only their own personal views.
  const canRenameView = (view: ITableViewWithColumns): boolean =>
    !view.isDefault &&
    (spreadsheetData.isAdmin || (view.isPersonalView && view.personalViewOwner?.publicId === user?.publicId))
  // The table page's + New view: the view menu's New … View choices on their own.
  const [newViewMenu, setNewViewMenu] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)

  // The view being renamed in its tab: the open one, or another renamed from its tab's menu.
  const viewRename = useInlineRename((viewId, name) =>
    updateView(
      'name',
      spreadsheetData,
      setSpreadsheetData,
      viewId,
      name,
      project.publicId,
      () => setSnackbarMessage({ status: 'success', message: 'View name updated!' }),
      (error: string) => displayErrorMessage(error)
    )
  )
  const renamingViewId = viewRename.renamingId
  const isEditingViewName = renamingViewId !== null

  // Inline rename of the table by double-clicking its name in the page header, or from
  // Table Settings' Rename Table: for its Owners (isAdmin, which an archived table never grants).
  const tableRename = useInlineRename((_tableId, name) =>
    updateTable(
      'name',
      spreadsheetData,
      setSpreadsheetData,
      name,
      project.publicId,
      () => setSnackbarMessage({ status: 'success', message: 'Table name updated!' }),
      (error: string) => displayErrorMessage(error)
    )
  )
  const startTableNameRename = (): void => {
    if (!spreadsheetData.isAdmin) return
    tableRename.start(spreadsheetData.tableDetails.publicId, spreadsheetData.tableDetails.name)
  }

  // The view menu, opened from a view tab: from the open view's own tab (or any
  // tab, right-clicked) it's led by that view's settings, which on the table page
  // live there, not in ⋯.
  const [viewMenuWithCurrent, setViewMenuWithCurrent] = useState<boolean>(false)
  const [viewMenuTargetId, setViewMenuTargetId] = useState<string | undefined>(undefined)
  const openViewMenu = (event: React.SyntheticEvent<HTMLElement>, withCurrent: boolean, targetViewId?: string) => {
    setViewMenuWithCurrent(withCurrent)
    setViewMenuTargetId(targetViewId)
    setMenuState('view', {
      open: true,
      top: `${event.currentTarget.getBoundingClientRect().bottom + 10}px`,
      left: `${event.currentTarget.getBoundingClientRect().left}px`,
      right: 'auto',
      bottom: 'auto'
    })
  }

  // Renames the open view unless another's id is given (from its tab's menu).
  const startViewNameRename = (viewId?: string) => {
    const view =
      spreadsheetData.tableView.find((tableView) => tableView.publicId === viewId) ?? spreadsheetData.viewDetails
    if (!canRenameView(view)) return
    // A double-click fires single clicks first, so the view menu may already have been
    // opened by this gesture — close it so it isn't left open behind the rename input.
    setMenuState('view', INITIAL_CONTEXT_MENU_STATE)
    viewRename.start(view.publicId, view.name)
  }

  // Compact-mode fallback: when the per-view toolbar button is hidden in the
  // view-config dropdown, the empty-state buttons can't `.click()` it. We
  // open the menu just below the toolbar's bottom edge at the right side —
  // visually consistent with where the dropdown would have anchored it.
  useEffect(() => {
    return subscribeOpenViewSettingsMenu((button) => {
      const padding = 16
      const gap = 4
      const toolbarEl = document.querySelector<HTMLElement>('[data-testid="spreadsheet-toolbar"]')
      const toolbarBottom = toolbarEl ? toolbarEl.getBoundingClientRect().bottom : 44
      const menuWidth = button === 'table-pivot-button' ? 300 : button === 'table-matrix-button' ? 400 : chartWidth
      const next = {
        open: true,
        top: `${toolbarBottom + gap}px`,
        left: `${Math.max(0, window.innerWidth - menuWidth - padding)}px`,
        right: 'auto',
        bottom: 'auto'
      }
      if (button === 'table-chart-button') {
        if (spreadsheetData.viewDetails.type === ViewTypes.CALENDAR) {
          setMenuState('calendar', next)
        } else {
          setMenuState('chart', next)
        }
      } else if (button === 'table-pivot-button') {
        setMenuState('pivot', next)
      } else if (button === 'table-matrix-button') {
        setMenuState('matrix', next)
      }
    })
  }, [spreadsheetData.viewDetails.type, chartWidth])

  const handleDeleteTableSuccess = () => {
    setSpreadsheetData({
      type: 'DELETE_TABLE'
    })
  }

  const handleDeleteTableFailure = (error: any) => {
    displayErrorMessage(error)
  }

  const handleDeleteTable = (): Promise<void> =>
    new Promise<void>((resolve, reject) => {
      deleteTable(
        project.publicId,
        spreadsheetData,
        () => {
          handleDeleteTableSuccess()
          resolve()
        },
        (err: any) => {
          handleDeleteTableFailure(err)
          reject(err)
        }
      )
    })

  const handleDeleteViewSuccess = async () => {
    setDeleteViewModal(false)
    const deletedViewId = viewToDelete ? viewToDelete.id : spreadsheetData.viewDetails.publicId

    if (inProcess && originalViewId) {
      setSpreadsheetData({ type: 'DELETE_VIEW', viewId: deletedViewId })

      // Change back to the props view
      setSpreadsheetData({
        type: 'SET_VIEW',
        viewId: originalViewId ? originalViewId : spreadsheetData.tableDetails.defaultViewId,
        inProcess
      })
      setSnackbarMessage({ status: 'success', message: 'The view was deleted successfully!' })
      return
    }

    // Change back to the default view
    // Only change back to the default view if the deleted view is the current view
    if (viewToDelete?.id === spreadsheetData.activeTableView) {
      const defaultViewId = spreadsheetData.tableDetails.defaultViewId
      // The default view may not have been hydrated if the user navigated directly to a
      // non-default view. Fetch it lazily before dispatching SET_VIEW so that columns are
      // available immediately and the spreadsheet renders correctly. If hydration fails,
      // abort the switch so we don't drop the user into a grid with columns: [].
      const tableViewWithoutDeleted = spreadsheetData.tableView.filter((v) => v.publicId !== deletedViewId)
      let result: HydrateViewResult
      try {
        result = await hydrateView(defaultViewId, tableViewWithoutDeleted)
      } catch (error) {
        displayErrorMessage(error)
        return
      }

      setSpreadsheetData({ type: 'DELETE_VIEW', viewId: deletedViewId })
      if (result.parent) {
        setSpreadsheetData({ type: 'HYDRATE_VIEW', viewDetails: result.parent })
      }
      setSpreadsheetData({
        type: 'SET_VIEW',
        viewId: defaultViewId,
        viewDetails: result.view,
        inProcess
      })
    } else {
      setSpreadsheetData({ type: 'DELETE_VIEW', viewId: deletedViewId })
    }

    navigate(
      generatePath(TABLE, {
        projectId: project.publicId,
        tableId: spreadsheetData.tableDetails.publicId,
        viewId: spreadsheetData.tableDetails.defaultViewId
      }),
      { replace: true }
    )
    setSnackbarMessage({ status: 'success', message: 'The view was deleted successfully!' })
  }

  const handleDeleteViewFailure = (error: any) => {
    displayErrorMessage(error)
  }

  const handleDeleteView = (): Promise<void> =>
    new Promise<void>((resolve, reject) => {
      deleteView(
        setSpreadsheetData,
        project.publicId,
        viewToDelete ? viewToDelete.id : spreadsheetData.viewDetails.publicId,
        () => {
          handleDeleteViewSuccess().then(resolve, reject)
        },
        (err: any) => {
          handleDeleteViewFailure(err)
          reject(err)
        }
      )
    })

  const successfulViewSave = () => {
    setSnackbarMessage({ status: 'success', message: 'The view has been saved!' })
  }

  const unsuccessfulViewSave = (error: any) => {
    displayErrorMessage(error)
  }

  const handleViewSave = () => {
    saveCurrentView(project.publicId, spreadsheetData, setSpreadsheetData, successfulViewSave, unsuccessfulViewSave)
  }

  const showColumn = (columnId: string) => {
    setSpreadsheetData({ type: 'SHOW_COLUMN', columnId })
  }

  // Grouping (and the tree-group leaf protection) only renders on spreadsheet views. It is
  // meaningless on synthetic-column views (transpose, matrix — the rendered rows/columns aren't
  // the originals) and on views that never render grouping at all (forms, charts, calendars),
  // even though those can inherit `groupSettings` / `treeGroupView` from the source view. Gate
  // the hide-menu group guards on the view type so inherited-but-unrendered grouping doesn't
  // make those columns unhideable (issue #10264).
  const supportsGrouping = viewSupportsGrouping(spreadsheetData.viewDetails.type)

  const treeLeafColumnId = supportsGrouping
    ? getTreeLeafColumnId(
        allColumns,
        spreadsheetData.userConfiguration.groupSettings,
        spreadsheetData.viewDetails.treeGroupView
      )
    : null

  // Pivot-column protection only applies when the data is actually being transposed
  // (coordinateMap populated). A TRANSPOSE view without a pivot renders un-transposed and
  // has no pivot column to protect.
  const transposePivotColumnId = getOriginalData(spreadsheetData).coordinateMap
    ? (spreadsheetData.viewDetails.transposeSettings?.pivotColumnId ?? null)
    : null

  const hideColumn = (columnId: string) => {
    if (supportsGrouping) {
      const isGrouped = spreadsheetData.userConfiguration.groupSettings.some((g) => g.columnId === columnId)
      if (isGrouped) {
        setSnackbarMessage({
          status: 'error',
          message: "This column groups the view, so it can't be hidden. Remove it from the grouping first."
        })
        return
      }
      if (treeLeafColumnId && columnId === treeLeafColumnId) {
        setSnackbarMessage({
          status: 'error',
          message: "The first column can't be hidden while the view is grouped as a tree."
        })
        return
      }
    }
    if (transposePivotColumnId && columnId === transposePivotColumnId) {
      setSnackbarMessage({ status: 'error', message: "The pivot column can't be hidden while the view is transposed." })
      return
    }
    setSpreadsheetData({ type: 'HIDE_COLUMN', columnId })
  }

  const unrequireColumn = (columnId: string) => {
    setSpreadsheetData({ type: 'UNREQUIRE_COLUMN', columnId })
  }

  const requireColumn = (columnId: string) => {
    const column = spreadsheetData.viewDetails.columns.find((column) => column.publicId === columnId)
    if (column) {
      const isBlocked = column.isJoined || column.scriptEnabled || column.formulaEnabled
      if (isBlocked) {
        setSnackbarMessage({
          status: 'error',
          message: `Column '${column.name}' can't be required because Morta calculates its values automatically.`
        })
      } else {
        setSpreadsheetData({ type: 'REQUIRE_COLUMN', columnId: column.publicId })
      }
    }
  }

  const allowOpenInNewTab =
    inProcess &&
    spreadsheetData.tableDetails.permissionLevel === PERMISSIONS.owner &&
    !spreadsheetData.viewDetails.isPersonalView

  // A Custom View is saved by its owners: the save keeps its filters, sorts and hidden columns (its page is published
  // from the banner over it). Nobody else can save one, as nobody can make a personal view of one.
  const isCustomView = spreadsheetData.viewDetails.type === ViewTypes.CUSTOM_VIEW
  const showSave = isCustomView
    ? spreadsheetData.isAdmin && !spreadsheetData.viewDetails.isPersonalView
    : (inProcess && spreadsheetData.viewDetails.type !== ViewTypes.FORM) || !inProcess
  const unsaved = hasUnsavedChanges(spreadsheetData)

  // Both save paths (PUT /views/<id> for "Save This View" and POST /table/<id>/views for
  // "Save New/Personal View") are decorated with @paid_subscription_required server-side,
  // so any free user click triggers the upgrade-request email — disable the icon for them
  // outright rather than waiting for the network round-trip.
  const saveDisabled = usePersonalSaveBlocked()

  if (!spreadsheetData.loading) {
    // Each control is defined once, then arranged: in a document an embedded table
    // keeps its one-row toolbar; on the table's own page the table-level actions
    // go in the page header and the rest in a view row beneath it.
    const tableName = (
      <Tooltip content={spreadsheetData.tableDetails.name} showOnlyIfTruncated delay={300}>
        <div data-testid="table-name" className={element({ class: 'ml-0!' })}>
          {allowOpenInNewTab ? (
            <Link
              to={generatePath(TABLE, {
                projectId: project.publicId,
                tableId: spreadsheetData.tableDetails.publicId,
                viewId: spreadsheetData.viewDetails.publicId
              })}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="font-bold">{spreadsheetData.tableDetails.name}</span>
            </Link>
          ) : (
            <span className="text-body font-bold">{spreadsheetData.tableDetails.name}</span>
          )}
        </div>
      </Tooltip>
    )
    const viewRenameInput = (
      <Input
        {...viewRename.inputProps}
        type="text"
        data-testid="view-rename-input"
        className="mr-auto! m-[5px] h-[26px] w-[180px] py-0 px-2 border border-blue"
      />
    )
    const viewPicker = isEditingViewName ? (
      viewRenameInput
    ) : (
      <Tooltip content={spreadsheetData.viewDetails.name} showOnlyIfTruncated delay={300}>
        <div
          data-testid="table-name-select"
          data-tour="view-selector"
          // The view's name is the one button with text: cut short to fit, unlike the icons beside it.
          className={button({ class: 'mr-auto! truncate' })}
          onClick={(event: React.MouseEvent) =>
            setMenuState('view', {
              open: true,
              top: `${event.currentTarget.getBoundingClientRect().bottom + 10}px`,
              left: `${event.currentTarget.getBoundingClientRect().left}px`,
              right: 'auto',
              bottom: 'auto'
            })
          }
          onDoubleClick={() => startViewNameRename()}
        >
          {spreadsheetData.viewDetails.name}
        </div>
      </Tooltip>
    )
    const viewConfigButton = isCompact ? (
      <Tooltip content="Change view configuration">
        <div
          data-testid="table-view-config-button"
          className={button()}
          onClick={(event: React.MouseEvent) =>
            setMenuState('viewConfig', {
              open: true,
              top: `${event.currentTarget.getBoundingClientRect().bottom + 10}px`,
              left: `${event.currentTarget.getBoundingClientRect().left}px`,
              right: 'auto',
              bottom: 'auto'
            })
          }
        >
          <ChevronDown className="w-5 h-5" />
        </div>
      </Tooltip>
    ) : null
    const chartButton = !isCompact && !spreadsheetData.tableDetails.isDeleted && capabilities.chartSettings && (
      <Tooltip
        content={
          spreadsheetData.viewDetails.type === ViewTypes.CALENDAR ? 'Change calendar settings' : 'Change chart settings'
        }
      >
        <div
          data-testid="table-chart-button"
          className={button({
            state: isLoading ? 'disabled' : activeActions.chart ? 'active' : undefined
          })}
          onClick={(event: React.MouseEvent) => {
            if (isLoading) {
              return
            }
            const next = {
              open: true,
              top: `${event.currentTarget.getBoundingClientRect().bottom + 10}px`,
              left: `${event.currentTarget.getBoundingClientRect().left - chartWidth + offset}px`,
              right: 'auto',
              bottom: 'auto'
            }
            if (spreadsheetData.viewDetails.type === ViewTypes.CALENDAR) {
              setMenuState('calendar', next)
              return
            }
            setMenuState('chart', next)
          }}
        >
          <Chart />
        </div>
      </Tooltip>
    )
    // A Custom View's owners edit it in the panel these open on the right: by asking MortaAI, or by going back to
    // one of its versions. Each button opens its own panel, or closes the panel if it's already showing.
    const showCustomViewButtons =
      !spreadsheetData.tableDetails.isDeleted &&
      spreadsheetData.viewDetails.type === ViewTypes.CUSTOM_VIEW &&
      spreadsheetData.isAdmin &&
      !spreadsheetData.viewDetails.isPersonalView
    const customViewPanelButton = (mode: CustomViewPanelMode, label: string, testId: string, icon: React.ReactNode) => {
      const active = customViewPanel.open && customViewPanel.mode === mode
      return (
        <Tooltip content={label}>
          <div
            data-testid={testId}
            role="button"
            aria-label={label}
            aria-pressed={active}
            className={button({ state: active ? 'active' : undefined })}
            tabIndex={0}
            onClick={() => toggleCustomViewPanel(mode)}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return
              event.preventDefault()
              toggleCustomViewPanel(mode)
            }}
          >
            {icon}
          </div>
        </Tooltip>
      )
    }
    const customViewAIButton =
      showCustomViewButtons &&
      customViewPanelButton('ai', 'Edit with MortaAI', 'table-custom-view-ai-button', <PencilSparkles />)
    // A Form's pen: Edit with MortaAI, as the view's menu has it: after asking, copies the form as a Custom View
    // and opens MortaAI's panel beside the copy.
    const formAIButton = canEditWithAi(
      spreadsheetData.viewDetails,
      spreadsheetData.isAdmin,
      !!spreadsheetData.tableDetails.isSynced,
      inProcess
    ) &&
      !spreadsheetData.tableDetails.isDeleted && (
        <Tooltip content="Edit with MortaAI">
          <div
            data-testid="table-form-ai-button"
            role="button"
            aria-label="Edit with MortaAI"
            aria-busy={copyingForAi || undefined}
            className={button({ state: copyingForAi ? 'disabled' : undefined })}
            tabIndex={0}
            onClick={() => askToEditWithAi(spreadsheetData.viewDetails)}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return
              event.preventDefault()
              askToEditWithAi(spreadsheetData.viewDetails)
            }}
          >
            {copyingForAi ? <Spinner className="size-4" /> : <PencilSparkles />}
          </div>
        </Tooltip>
      )
    const customViewVersionsButton =
      showCustomViewButtons &&
      customViewPanelButton('versions', 'Versions', 'table-custom-view-versions-button', <History />)

    const pivotButton = !isCompact && !spreadsheetData.tableDetails.isDeleted && capabilities.pivotSettings && (
      <Tooltip content="Change pivot column">
        <div
          data-testid="table-pivot-button"
          className={button({
            state: isLoading ? 'disabled' : activeActions.pivot ? 'active' : undefined
          })}
          onClick={(event: React.MouseEvent) => {
            if (isLoading) {
              return
            }
            setMenuState('pivot', {
              open: true,
              top: `${event.currentTarget.getBoundingClientRect().bottom + 10}px`,
              left: `${event.currentTarget.getBoundingClientRect().left}px`,
              right: 'auto',
              bottom: 'auto'
            })
          }}
        >
          <Grid />
        </div>
      </Tooltip>
    )
    const matrixButton = !isCompact && !spreadsheetData.tableDetails.isDeleted && capabilities.matrixSettings && (
      <Tooltip content="Configure matrix view">
        <div
          data-testid="table-matrix-button"
          className={button({
            state: isLoading ? 'disabled' : activeActions.matrix ? 'active' : undefined
          })}
          onClick={(event: React.MouseEvent) => {
            if (isLoading) {
              return
            }
            setMenuState('matrix', {
              open: true,
              top: `${event.currentTarget.getBoundingClientRect().bottom + 10}px`,
              left: `${event.currentTarget.getBoundingClientRect().left}px`,
              right: 'auto',
              bottom: 'auto'
            })
          }}
        >
          <Grid />
        </div>
      </Tooltip>
    )
    const colorButton = !isCompact && !spreadsheetData.tableDetails.isDeleted && capabilities.colour && (
      <Tooltip content="Conditional formatting">
        <div
          id="colour-toolbar-button"
          data-testid="table-color-button"
          data-tour="table-colour-button"
          data-active={noOfColours > 0 || undefined}
          className={button({ state: isLoading ? 'disabled' : activeActions.colour ? 'active' : undefined })}
          onClick={(event: React.MouseEvent) => {
            if (isLoading) {
              return
            }
            setColourMenu({
              open: true,
              ...panelAnchor(event.currentTarget, colourWidth),
              right: 'auto',
              bottom: 'auto'
            })
          }}
        >
          {withCount(<Colour />, noOfColours)}
        </div>
      </Tooltip>
    )
    const filterButton = !isCompact && !spreadsheetData.tableDetails.isDeleted && capabilities.filter && (
      <Tooltip content={noOfSlicerFilters > 0 ? 'Filter rows (slicer active)' : 'Filter rows'}>
        <div
          id="filter-toolbar-button"
          data-testid="table-filter-button"
          data-tour="table-filter-button"
          className={button({
            state: isLoading ? 'disabled' : activeActions.filter ? 'active' : undefined
          })}
          onClick={(event: React.MouseEvent) => {
            if (isLoading) {
              return
            }
            setFilterMenu({
              open: true,
              ...panelAnchor(event.currentTarget, filterWidth),
              right: 'auto',
              bottom: 'auto'
            })
          }}
        >
          {withCount(<Filter />, noOfFilters + noOfSlicerFilters + metadataFilters)}
        </div>
      </Tooltip>
    )
    const sortButton = !isCompact && !spreadsheetData.tableDetails.isDeleted && capabilities.sort && (
      <Tooltip content="Sort rows">
        <div
          className={button({ state: isLoading ? 'disabled' : activeActions.sort ? 'active' : undefined })}
          data-testid="table-sort-button"
          data-tour="table-sort-button"
          onClick={(event: React.MouseEvent) => {
            if (isLoading) {
              return
            }
            setMenuState('sort', {
              open: true,
              ...panelAnchor(event.currentTarget, sortWidth),
              right: 'auto',
              bottom: 'auto'
            })
          }}
        >
          {withCount(<Sort />, noOfSorts)}
        </div>
      </Tooltip>
    )
    const groupButton = !isCompact && !spreadsheetData.tableDetails.isDeleted && capabilities.group && (
      <Tooltip content="Group rows">
        <div
          id="group-toolbar-button"
          data-testid="table-group-button"
          data-tour="table-group-button"
          className={button({ state: isLoading ? 'disabled' : activeActions.group ? 'active' : undefined })}
          onClick={(event: React.MouseEvent) => {
            if (isLoading) {
              return
            }
            setGroupMenu({
              open: true,
              ...panelAnchor(event.currentTarget, groupWidth),
              right: 'auto',
              bottom: 'auto'
            })
          }}
        >
          {withCount(<Group />, noOfGroups)}
        </div>
      </Tooltip>
    )
    const hideButton = !isCompact &&
      !spreadsheetData.tableDetails.isDeleted &&
      isCapabilityEnabled(capabilities.hide, spreadsheetData.isAdmin) && (
        <Tooltip content="Hide columns">
          <div
            data-testid="table-hide-button"
            data-tour="table-hide-button"
            className={button({
              state: activeActions.hide ? 'active' : undefined,
              class: spreadsheetData.viewDetails.type === ViewTypes.FORM ? 'ml-auto' : ''
            })}
            onClick={(event: React.MouseEvent) =>
              setMenuState('hide', {
                open: true,
                ...panelAnchor(event.currentTarget, hideWidth),
                right: 'auto',
                bottom: 'auto'
              })
            }
          >
            {withCount(<Visible />, noOfHidden)}
          </div>
        </Tooltip>
      )
    const importButton = !isCompact && canImportData(spreadsheetData) && (
      <Tooltip content="Import data">
        <div
          data-testid="table-import-button"
          data-tour="table-import-button"
          className={button({
            state:
              spreadsheetData.tableDetails.isSynced && spreadsheetData.tableDetails.failedSyncAttempts > 0
                ? 'error'
                : spreadsheetData.tableDetails.isSynced
                  ? 'active'
                  : undefined
          })}
          onClick={(event: React.MouseEvent) =>
            setMenuState('import', {
              open: true,
              top: `${event.currentTarget.getBoundingClientRect().bottom + 10}px`,
              left: `${event.currentTarget.getBoundingClientRect().left - importWidth + offset}px`,
              right: 'auto',
              bottom: 'auto'
            })
          }
        >
          <ImportData />
        </div>
      </Tooltip>
    )
    const exportButton = !isCompact && capabilities.exportData && (project.allowTableExport || projectAdmin) && (
      <Tooltip content="Export data">
        <div
          data-testid="table-export-button"
          data-tour="table-export-button"
          className={button()}
          onClick={(event: React.MouseEvent) =>
            setMenuState('export', {
              open: true,
              ...panelAnchor(event.currentTarget, exportWidth),
              right: 'auto',
              bottom: 'auto'
            })
          }
        >
          <ExportData />
        </div>
      </Tooltip>
    )
    const joinsButton = !isCompact &&
      isCapabilityEnabled(capabilities.joins, spreadsheetData.isAdmin) &&
      !spreadsheetData.tableDetails.isDeleted && (
        <Tooltip content="Lookup data from other tables">
          <div
            data-testid="table-joins-button"
            data-tour="table-joins-button"
            className={button({
              state: activeActions.joins ? 'active' : undefined,
              class: spreadsheetData.viewDetails.type === ViewTypes.FORM ? 'ml-auto' : ''
            })}
            onClick={() => {
              setJoinsFocus(undefined)
              setJoinsModal(true)
            }}
          >
            <Join />
          </div>
        </Tooltip>
      )
    // On the table's page Audit sits in the page header, which has room for it
    // whatever the view row is doing; only the one-row toolbar folds it away.
    const historyButton = (!isCompact || !inProcess) &&
      spreadsheetData.isAdmin &&
      !spreadsheetData.tableDetails.isDeleted && (
        <Tooltip content="Audit history and backups">
          <div
            data-testid="table-history-button"
            className={button()}
            onClick={(event: React.MouseEvent) =>
              setMenuState('history', {
                open: true,
                top: `${event.currentTarget.getBoundingClientRect().bottom + 10}px`,
                left: `${event.currentTarget.getBoundingClientRect().left - historyWidth + 20}px`,
                right: 'auto',
                bottom: 'auto'
              })
            }
          >
            <History />
          </div>
        </Tooltip>
      )
    const lockButton = !isCompact && spreadsheetData.isAdmin && capabilities.lock && (
      <Tooltip content="Lock rows">
        <div
          data-testid="table-lock-button"
          className={button({ state: activeActions.lock ? 'active' : undefined })}
          onClick={(event: React.MouseEvent) =>
            setMenuState('lock', {
              open: true,
              ...panelAnchor(event.currentTarget, lockWidth),
              right: 'auto',
              bottom: 'auto'
            })
          }
        >
          {withCount(<Lock />, noOfLocked)}
        </div>
      </Tooltip>
    )
    const requiredButton = !isCompact &&
      spreadsheetData.isAdmin &&
      spreadsheetData.viewDetails.type === ViewTypes.FORM && (
        <Tooltip content="Required fields">
          <div
            data-testid="table-required-button"
            className={button({ state: activeActions.required ? 'active' : undefined })}
            onClick={(event: React.MouseEvent) =>
              setMenuState('required', {
                open: true,
                ...panelAnchor(event.currentTarget, requiredWidth),
                right: 'auto',
                bottom: 'auto'
              })
            }
          >
            {withCount(<Asterisk />, noOfRequired)}
          </div>
        </Tooltip>
      )
    const columnOrderButton = !isCompact && !spreadsheetData.tableDetails.isDeleted && capabilities.columnOrder && (
      <Tooltip content="Change column order">
        <div
          data-testid="table-column-order-button"
          className={button()}
          onClick={(event: React.MouseEvent) =>
            setMenuState('columnOrder', {
              open: true,
              ...panelAnchor(event.currentTarget, columnOrderWidth),
              right: 'auto',
              bottom: 'auto'
            })
          }
        >
          <MoveColumn />
        </div>
      </Tooltip>
    )
    const freezeButton = !isCompact && !spreadsheetData.tableDetails.isDeleted && capabilities.freeze && (
      <Tooltip content="Freeze columns in place">
        <div
          data-testid="table-freeze-button"
          className={button({ state: activeActions.freeze ? 'active' : undefined })}
          onClick={(event: React.MouseEvent) =>
            setMenuState('freeze', {
              open: true,
              ...panelAnchor(event.currentTarget, freezeWidth),
              right: 'auto',
              bottom: 'auto'
            })
          }
        >
          {withCount(<Freeze />, Math.max(0, spreadsheetData.viewDetails.frozenIndex))}
        </div>
      </Tooltip>
    )
    const rowHeightButton = !isCompact && !spreadsheetData.tableDetails.isDeleted && capabilities.rowHeight && (
      <Tooltip content="Change row height">
        <div
          data-testid="table-row-height-button"
          className={button()}
          onClick={(event: React.MouseEvent) =>
            setMenuState('rowHeight', {
              open: true,
              ...panelAnchor(event.currentTarget, rowHeightWidth),
              right: 'auto',
              bottom: 'auto'
            })
          }
        >
          {spreadsheetData.viewDetails.rowHeight === rowHeights.Small && <SmallRowHeight />}
          {spreadsheetData.viewDetails.rowHeight === rowHeights.Medium && <MediumRowHeight />}
          {spreadsheetData.viewDetails.rowHeight === rowHeights.Tall && <TallRowHeight />}
          {spreadsheetData.viewDetails.rowHeight === rowHeights['Extra Tall'] && <ExtraTallRowHeight />}
        </div>
      </Tooltip>
    )
    const linkButton = allowOpenInNewTab && (
      <Tooltip content="Open in new tab">
        <div data-testid="table-link-button" className={button()}>
          <Link
            to={generatePath(TABLE, {
              projectId: project.publicId,
              tableId: spreadsheetData.tableDetails.publicId,
              viewId: spreadsheetData.viewDetails.publicId
            })}
            target="_blank"
            rel="noopener noreferrer"
            className="text-body flex items-center"
          >
            <LinkIcon />
          </Link>
        </div>
      </Tooltip>
    )
    const permissionsButton = spreadsheetData.isAdmin && !spreadsheetData.tableDetails.isDeleted && (
      <Tooltip content="Change permissions">
        <div
          className={button({
            class: spreadsheetData.viewDetails.type === ViewTypes.FORM ? 'ml-auto' : ''
          })}
          data-testid="table-permissions-button"
          data-tour="table-permissions-button"
          onClick={() => setPermissionsModal(true)}
        >
          <Permissions />
        </div>
      </Tooltip>
    )
    // Saving from the button or from View actions: the save menu, opened where it's asked for,
    // except for a personal view in a document, which saves straight away.
    const saveView = (at: { top: string; left: string }) => {
      if (saveDisabled) return
      if (!inProcess || !spreadsheetData.viewDetails.isPersonalView) {
        setMenuState('save', { open: true, top: at.top, left: at.left, right: 'auto', bottom: 'auto' })
      } else {
        handleViewSave()
      }
    }
    const saveButton = showSave && (
      <Tooltip content={saveDisabled ? getSaveBlockedTooltip(inProcess) : 'Save changes to view'}>
        <div
          data-testid="table-save-button"
          data-tour="table-save-button"
          className={saveDisabled ? button({ state: 'disabled' }) : unsaved ? pulse() : button()}
          onClick={(event: React.MouseEvent) =>
            saveView({
              top: `${event.currentTarget.getBoundingClientRect().bottom + 10}px`,
              left: `${event.currentTarget.getBoundingClientRect().left - saveWidth + 20}px`
            })
          }
        >
          <Save />
        </div>
      </Tooltip>
    )
    const searchButton = spreadsheetData.viewDetails.type === ViewTypes.SPREADSHEET && (
      <Tooltip content="Search for data">
        <div
          data-testid="table-search-button"
          data-tour="table-search-button"
          className={button({
            state: isLoading ? 'disabled' : activeActions.search ? 'active' : undefined
          })}
          onClick={(event: React.MouseEvent) => {
            if (isLoading) {
              return
            }
            setMenuState('search', {
              open: true,
              top: `${event.currentTarget.getBoundingClientRect().bottom + 10}px`,
              left: `${event.currentTarget.getBoundingClientRect().left - searchWidth + 20}px`,
              right: 'auto',
              bottom: 'auto'
            })
          }}
        >
          <Search />
        </div>
      </Tooltip>
    )
    const expandButton = !fullScreen && inProcess && (
      <Tooltip content="View full screen">
        <div data-testid="table-expand-button" className={button()} onClick={() => setFullScreen(true)}>
          <Expand />
        </div>
      </Tooltip>
    )
    const collapseButton = fullScreen && (
      <Tooltip content="Exit full screen">
        <div data-testid="table-collapse-button" className={button()} onClick={() => setFullScreen(false)}>
          <Cross />
        </div>
      </Tooltip>
    )
    const settingsButton = spreadsheetData.isAdmin && (
      <Tooltip content="More options">
        <div
          data-testid="table-settings-button"
          className={button()}
          onClick={(event: React.MouseEvent) =>
            setMenuState('settings', {
              open: true,
              ...panelAnchor(event.currentTarget, settingsWidth),
              right: 'auto',
              bottom: 'auto'
            })
          }
        >
          <Options />
        </div>
      </Tooltip>
    )
    const menusAndModals = (
      <>
        {editWithAiDialog}
        {newViewMenu.open && (
          <ViewMenu
            id={`new-view-menu-${uniqueNumber}`}
            createOnly
            menuState={newViewMenu}
            setMenuState={setNewViewMenu}
            width={260}
            onDeleteView={() => undefined}
          />
        )}

        {menuStateOf('view').open && (
          <ViewMenu
            id={`view-context-menu-${uniqueNumber}`}
            menuState={menuStateOf('view')}
            setMenuState={setMenuStateFor('view')}
            width={viewMenuWithCurrent ? viewWidth : VIEW_LIST_MAX_WIDTH}
            minWidth={viewMenuWithCurrent ? undefined : VIEW_LIST_MIN_WIDTH}
            onDeleteView={(viewId: string, viewName: string) => {
              setViewToDelete({ id: viewId, name: viewName })
              setDeleteViewModal(true)
            }}
            withCurrentView={viewMenuWithCurrent}
            targetViewId={viewMenuTargetId}
            onRenameView={startViewNameRename}
          />
        )}

        {colourMenu.open && (
          <ColourMenu
            id={`colour-context-menu-${uniqueNumber}`}
            menuState={colourMenu}
            setMenuState={setColourMenu}
            width={colourWidth}
          />
        )}

        {filterMenu.open && (
          <FilterMenu
            id={`filter-context-menu-${uniqueNumber}`}
            menuState={filterMenu}
            setMenuState={setFilterMenu}
            width={filterWidth}
          />
        )}

        {menuStateOf('sort').open && (
          <SortMenu
            id={`sort-context-menu-${uniqueNumber}`}
            menuState={menuStateOf('sort')}
            setMenuState={setMenuStateFor('sort')}
            width={sortWidth}
          />
        )}

        {groupMenu.open && (
          <GroupMenu
            id={`group-context-menu-${uniqueNumber}`}
            menuState={groupMenu}
            setMenuState={setGroupMenu}
            width={groupWidth}
            handleToggleCollapseAll={handleToggleCollapseAll}
          />
        )}

        {menuStateOf('hide').open && (
          <HideMenu
            id={`hide-context-menu-${uniqueNumber}`}
            menuState={menuStateOf('hide')}
            setMenuState={setMenuStateFor('hide')}
            width={hideWidth}
            columns={visibleHideColumns}
            hiddenColumnIds={spreadsheetData.userConfiguration.hiddenColumns}
            cantHideReason={(columnId) => {
              if (
                supportsGrouping &&
                spreadsheetData.userConfiguration.groupSettings.some((g) => g.columnId === columnId)
              )
                return 'Cannot hide a grouped column'
              if (supportsGrouping && treeLeafColumnId === columnId)
                return 'Cannot hide the first column in tree group view'
              if (transposePivotColumnId === columnId) return 'Cannot hide the pivot column in transpose view'
              return undefined
            }}
            onHide={hideColumn}
            onShow={showColumn}
          />
        )}

        {menuStateOf('lock').open && (
          <LockMenu
            id={`lock-context-menu-${uniqueNumber}`}
            menuState={menuStateOf('lock')}
            setMenuState={setMenuStateFor('lock')}
            width={lockWidth}
          />
        )}

        {menuStateOf('required').open && (
          <RequiredMenu
            id={`required-context-menu-${uniqueNumber}`}
            menuState={menuStateOf('required')}
            setMenuState={setMenuStateFor('required')}
            width={requiredWidth}
            columns={visibleRequiredColumns}
            cantRequireReason={(columnId) => {
              const column = visibleRequiredColumns.find((column) => column.publicId === columnId)
              return column?.scriptEnabled || column?.formulaEnabled
                ? "Morta calculates this column's values, so it can't be required."
                : undefined
            }}
            onRequire={requireColumn}
            onUnrequire={unrequireColumn}
          />
        )}

        {menuStateOf('columnOrder').open && (
          <ColumnOrderMenu
            id={`column-order-context-menu-${uniqueNumber}`}
            menuState={menuStateOf('columnOrder')}
            setMenuState={setMenuStateFor('columnOrder')}
            width={columnOrderWidth}
          />
        )}

        {menuStateOf('history').open && (
          <HistoryMenu
            id={`history-context-menu-${uniqueNumber}`}
            menuState={menuStateOf('history')}
            setMenuState={setMenuStateFor('history')}
            width={historyWidth}
            setBackupModal={(open: boolean) => {
              setBackupModal(open)
              if (open) setMenuState('history', INITIAL_CONTEXT_MENU_STATE)
            }}
            setAuditModal={(open: boolean) => {
              setAuditModal(open)
              if (open) setMenuState('history', INITIAL_CONTEXT_MENU_STATE)
            }}
          />
        )}

        {menuStateOf('freeze').open && (
          <FreezeMenu
            id={`freeze-context-menu-${uniqueNumber}`}
            menuState={menuStateOf('freeze')}
            setMenuState={setMenuStateFor('freeze')}
            width={freezeWidth}
          />
        )}

        {menuStateOf('rowHeight').open && (
          <RowHeightMenu
            id={`row-height-context-menu-${uniqueNumber}`}
            menuState={menuStateOf('rowHeight')}
            setMenuState={setMenuStateFor('rowHeight')}
            width={rowHeightWidth}
          />
        )}

        {menuStateOf('save').open && (
          <SaveMenu
            id={`save-context-menu-${uniqueNumber}`}
            menuState={menuStateOf('save')}
            setMenuState={setMenuStateFor('save')}
            width={saveWidth}
          />
        )}

        {menuStateOf('search').open && (
          <SearchMenu
            id={`search-context-menu-${uniqueNumber}`}
            menuState={menuStateOf('search')}
            setMenuState={setMenuStateFor('search')}
            width={searchWidth}
            columns={columns}
          />
        )}

        {menuStateOf('import').open && (
          <ImportMenu
            id={`import-context-menu-${uniqueNumber}`}
            menuState={menuStateOf('import')}
            setMenuState={setMenuStateFor('import')}
            width={importWidth}
            setAppendFileModal={setAppendFileModal}
            setImportFromTableModal={setImportFromTableModal}
            openIntegration={openIntegration}
          />
        )}

        {menuStateOf('export').open && (
          <ExportMenu
            id={`export-context-menu-${uniqueNumber}`}
            menuState={menuStateOf('export')}
            setMenuState={setMenuStateFor('export')}
            width={exportWidth}
          />
        )}

        {permissionsModal && (
          <PermissionsModal
            id="spreadsheet-permissions-modal"
            open={permissionsModal}
            setOpen={setPermissionsModal}
            onClose={() => setMenuState('settings', INITIAL_CONTEXT_MENU_STATE)}
          />
        )}

        {menuStateOf('settings').open && (
          <SettingsMenu
            id={`settings-context-menu-${uniqueNumber}`}
            menuState={menuStateOf('settings')}
            setMenuState={setMenuStateFor('settings')}
            width={settingsWidth}
            setDeleteTableModal={(open: boolean) => {
              setDeleteTableModal(open)
              if (open) setMenuState('settings', INITIAL_CONTEXT_MENU_STATE)
            }}
            setDuplicateTableModal={(open: boolean) => {
              setDuplicateTableModal(open)
              if (open) setMenuState('settings', INITIAL_CONTEXT_MENU_STATE)
            }}
            setRelatedModal={(open: boolean) => {
              setRelatedModal(open)
              if (open) setMenuState('settings', INITIAL_CONTEXT_MENU_STATE)
            }}
            onRenameTable={startTableNameRename}
            setViewDuplicatesModal={(open: boolean) => {
              setViewDuplicatesModal(open)
              if (open) setMenuState('settings', INITIAL_CONTEXT_MENU_STATE)
            }}
          />
        )}

        {menuStateOf('chart').open && (
          <ChartMenu
            id={`chart-context-menu-${uniqueNumber}`}
            menuState={menuStateOf('chart')}
            setMenuState={setMenuStateFor('chart')}
            width={chartWidth}
          />
        )}

        {menuStateOf('calendar').open && (
          <CalendarMenu
            id={`calendar-context-menu-${uniqueNumber}`}
            menuState={menuStateOf('calendar')}
            setMenuState={setMenuStateFor('calendar')}
            width={chartWidth}
          />
        )}

        {menuStateOf('pivot').open && (
          <PivotMenu
            id={`pivot-menu-${uniqueNumber}`}
            menuState={menuStateOf('pivot')}
            setMenuState={setMenuStateFor('pivot')}
            width={300}
          />
        )}

        {menuStateOf('matrix').open && (
          <MatrixMenu
            id={`matrix-menu-${uniqueNumber}`}
            menuState={menuStateOf('matrix')}
            setMenuState={setMenuStateFor('matrix')}
            width={400}
          />
        )}

        {menuStateOf('viewConfig').open && (
          <ViewConfigMenu
            id={`view-config-menu-${uniqueNumber}`}
            menuState={menuStateOf('viewConfig')}
            setMenuState={setMenuStateFor('viewConfig')}
            width={viewConfigWidth}
            tableId={spreadsheetData.tableDetails.publicId}
            viewId={spreadsheetData.viewDetails.publicId}
            viewType={spreadsheetData.viewDetails.type}
            isAdmin={spreadsheetData.isAdmin}
            isDeleted={spreadsheetData.tableDetails.isDeleted}
            canImport={canImportData(spreadsheetData)}
            allowExport={project.allowTableExport || projectAdmin}
            inProcess={inProcess}
            isPersonalView={spreadsheetData.viewDetails.isPersonalView}
            onChartClick={() => {
              const next = {
                open: true,
                ...fromViewConfig(chartWidth),
                right: 'auto',
                bottom: 'auto'
              }
              if (spreadsheetData.viewDetails.type === ViewTypes.CALENDAR) {
                setMenuState('calendar', next)
                return
              }
              setMenuState('chart', next)
            }}
            active={activeActions}
            groups={
              inProcess
                ? undefined
                : VIEW_ROW_GROUPS.map((group) => ({
                    label: group.label,
                    items: GROUP_MENU_ITEMS[group.id] ?? []
                  }))
            }
            onPivotClick={() =>
              setMenuState('pivot', {
                open: true,
                ...fromViewConfig(300),
                right: 'auto',
                bottom: 'auto'
              })
            }
            onSearchClick={() =>
              setMenuState('search', {
                open: true,
                ...fromViewConfig(searchWidth),
                right: 'auto',
                bottom: 'auto'
              })
            }
            onMatrixClick={() =>
              setMenuState('matrix', {
                open: true,
                ...fromViewConfig(400),
                right: 'auto',
                bottom: 'auto'
              })
            }
            onColourClick={() =>
              setColourMenu({
                open: true,
                ...fromViewConfig(colourWidth),
                right: 'auto',
                bottom: 'auto'
              })
            }
            onFilterClick={() =>
              setFilterMenu({
                open: true,
                ...fromViewConfig(filterWidth),
                right: 'auto',
                bottom: 'auto'
              })
            }
            onSortClick={() =>
              setMenuState('sort', {
                open: true,
                ...fromViewConfig(sortWidth),
                right: 'auto',
                bottom: 'auto'
              })
            }
            onGroupClick={() =>
              setGroupMenu({
                open: true,
                ...fromViewConfig(groupWidth),
                right: 'auto',
                bottom: 'auto'
              })
            }
            onHideClick={() =>
              setMenuState('hide', {
                open: true,
                ...fromViewConfig(hideWidth),
                right: 'auto',
                bottom: 'auto'
              })
            }
            onImportClick={() =>
              setMenuState('import', {
                open: true,
                ...fromViewConfig(importWidth),
                right: 'auto',
                bottom: 'auto'
              })
            }
            // Only the table page's View actions lists Save: an embedded table keeps its own Save
            // button beside this menu, so it would be there twice.
            onSaveClick={showSave && !inProcess ? () => saveView(fromViewConfig(saveWidth)) : undefined}
            saveDisabled={saveDisabled}
            onExportClick={() =>
              setMenuState('export', {
                open: true,
                ...fromViewConfig(exportWidth),
                right: 'auto',
                bottom: 'auto'
              })
            }
            onJoinsClick={() => setJoinsModal(true)}
            onHistoryClick={() =>
              setMenuState('history', {
                open: true,
                ...fromViewConfig(historyWidth),
                right: 'auto',
                bottom: 'auto'
              })
            }
            onLockClick={() =>
              setMenuState('lock', {
                open: true,
                ...fromViewConfig(lockWidth),
                right: 'auto',
                bottom: 'auto'
              })
            }
            onRequiredClick={() =>
              setMenuState('required', {
                open: true,
                ...fromViewConfig(requiredWidth),
                right: 'auto',
                bottom: 'auto'
              })
            }
            onColumnOrderClick={() =>
              setMenuState('columnOrder', {
                open: true,
                ...fromViewConfig(columnOrderWidth),
                right: 'auto',
                bottom: 'auto'
              })
            }
            onFreezeClick={() =>
              setMenuState('freeze', {
                open: true,
                ...fromViewConfig(freezeWidth),
                right: 'auto',
                bottom: 'auto'
              })
            }
            onRowHeightClick={() =>
              setMenuState('rowHeight', {
                open: true,
                ...fromViewConfig(rowHeightWidth),
                right: 'auto',
                bottom: 'auto'
              })
            }
          />
        )}

        {auditModal && (
          <AuditModal
            id="spreadsheet-audit-modal"
            open={auditModal}
            setOpen={setAuditModal}
            onClose={() => {
              setMenuState('settings', INITIAL_CONTEXT_MENU_STATE)
            }}
          />
        )}
        {deleteTableModal && (
          <DeleteResource
            id="delete-table-modal"
            open={deleteTableModal}
            setOpen={setDeleteTableModal}
            resourceName={spreadsheetData.tableDetails.name}
            onClose={() => {
              setMenuState('settings', INITIAL_CONTEXT_MENU_STATE)
            }}
            deleteResource={handleDeleteTable}
            otherContent={<ArchiveExplanation kind="table" />}
          />
        )}
        {deleteViewModal && (
          <DeleteResource
            id="delete-view-modal"
            open={deleteViewModal}
            setOpen={(open: boolean) => {
              setDeleteViewModal(open)
              if (!open) setViewToDelete(null)
            }}
            resourceName={viewToDelete?.name || spreadsheetData.viewDetails.name}
            onClose={() => {
              setMenuState('settings', INITIAL_CONTEXT_MENU_STATE)
              setViewToDelete(null)
            }}
            deleteResource={handleDeleteView}
            otherContent={
              <div className="mt-5">
                If you delete this view, the table data will be preserved, but all view information (filters, sortings,
                groupings etc.) will be deleted.
              </div>
            }
          />
        )}

        {joinsModal && (
          <TableLookupsDialog
            open={joinsModal}
            setOpen={(open) => {
              setJoinsModal(open)
              if (!open) setJoinsFocus(undefined)
            }}
            focusJoinId={joinsFocus}
          />
        )}
        {relatedModal && (
          <Related
            open={relatedModal}
            setOpen={setRelatedModal}
            type="table"
            publicId={spreadsheetData.tableDetails.publicId}
          />
        )}
        {viewDuplicatesModal && (
          <DuplicateChildren
            id="view-duplicates-modal"
            open={viewDuplicatesModal}
            setOpen={setViewDuplicatesModal}
            publicId={spreadsheetData.tableDetails.publicId}
            type="table"
          />
        )}
        {backupModal && <BackupModal id="backup-modal" open={backupModal} setOpen={setBackupModal} />}
        {(appendFileModal || importFromTableModal) && (
          <AppendFileModal
            id="append-csv-modal"
            open={appendFileModal || importFromTableModal}
            setOpen={() => {
              setAppendFileModal(false)
              setImportFromTableModal(false)
            }}
            fromTable={importFromTableModal}
          />
        )}
        {integration.open && (
          <IntegrationModal
            id="integration-modal"
            open={integration.open}
            setOpen={closeIntegration}
            provider={integration.provider!}
            integrationName={integration.name!}
            dataType={integration.syncType!}
          />
        )}

        {duplicateTableModal && (
          <Duplicate
            id={`duplicate-table-${uniqueNumber}`}
            open={duplicateTableModal}
            setOpen={setDuplicateTableModal}
            type="table"
            publicId={spreadsheetData.tableDetails.publicId}
            inProcess={inProcess}
          />
        )}
      </>
    )

    const viewRowButtons: Record<string, React.ReactNode[]> = {
      'view-settings': [
        customViewAIButton,
        formAIButton,
        customViewVersionsButton,
        chartButton,
        pivotButton,
        matrixButton
      ],
      'bring-in': [importButton, joinsButton],
      find: [searchButton, filterButton, sortButton, groupButton],
      layout: [freezeButton, hideButton, rowHeightButton, columnOrderButton],
      // A form's required fields beside its locks: both say what can be entered.
      rules: [colorButton, lockButton, requiredButton],
      'export-save': [exportButton, saveButton]
    }
    const viewRowGroups = VIEW_ROW_GROUPS.map((group) => ({ ...group, items: viewRowButtons[group.id] }))

    if (!inProcess) {
      return (
        <div
          className="flex flex-col w-full shrink-0 box-border select-none"
          data-testid="spreadsheet-toolbar"
          onMouseDown={resetSpreadsheetClick}
        >
          <PageHeader
            name={spreadsheetData.tableDetails.name}
            resource={{ kind: 'table', publicId: spreadsheetData.tableDetails.publicId }}
            testIdPrefix="table"
            titleTestId="table-name"
            renameInput={
              tableRename.renamingId !== null ? (
                <Input
                  {...tableRename.inputProps}
                  type="text"
                  data-testid="table-rename-input"
                  aria-label="Table name"
                  className="min-w-0 h-[26px] w-[240px] py-0 px-2 border border-blue font-semibold"
                />
              ) : undefined
            }
            onRename={startTableNameRename}
          >
            <div className={PAGE_HEADER_ACTIONS}>
              <ToolbarGroup label="History and access">
                {historyButton}
                {permissionsButton}
              </ToolbarGroup>
              <ToolbarGroup label="More">{settingsButton}</ToolbarGroup>
            </div>
          </PageHeader>

          {/* The view row: the views on the left, what acts on the open view on
              the right, grouped by what it does. The actions keep a fixed room
              (a spreadsheet view's, up to half the row), folding groups away
              past that, so the views beside them have the same room whichever
              view is open. */}
          <div
            ref={toolbarRowRef}
            className="flex items-center w-full h-10 px-[5px] bg-surface shadow-[inset_0_-1px_0_var(--color-divider)] overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            data-testid="table-view-row"
            data-groups={viewRowGroups
              .filter((group) => group.items.some(Boolean))
              .map((group) => group.id)
              .join(' ')}
          >
            <ViewTabs
              renameInput={isEditingViewName ? viewRenameInput : undefined}
              renamingViewId={renamingViewId}
              onRenameCurrent={() => startViewNameRename()}
              onAllViews={(event) => openViewMenu(event, false)}
              onCurrentView={(event) => openViewMenu(event, true)}
              onViewMenu={(event, viewId) => openViewMenu(event, true, viewId)}
              onNewView={(event) =>
                setNewViewMenu({
                  open: true,
                  top: `${event.currentTarget.getBoundingClientRect().bottom + 10}px`,
                  left: `${event.currentTarget.getBoundingClientRect().left}px`,
                  right: 'auto',
                  bottom: 'auto'
                })
              }
            />
            {/* The view's actions sit on the right, in the strongest text colour
                (near-black, or near-white in dark mode). A toggle that's on
                stays blue. */}
            <div
              className="flex shrink-0 justify-end max-w-1/2"
              style={{ width: VIEW_ACTIONS_WIDTH }}
              data-testid="table-view-actions"
            >
              <div className={`${PAGE_HEADER_ACTIONS} text-body`}>
                {viewRowGroups
                  .filter((group) => !foldedGroups.includes(group.id))
                  .map((group) => (
                    <ToolbarGroup key={group.id} id={group.id} label={group.label}>
                      {group.items.filter(Boolean).map((item, index) => (
                        <React.Fragment key={index}>{item}</React.Fragment>
                      ))}
                    </ToolbarGroup>
                  ))}
                <ToolbarGroup label="View actions">
                  <Tooltip content={foldedActive ? 'All view actions (some folded here are on)' : 'All view actions'}>
                    <div
                      data-testid="table-view-more-button"
                      data-folded-active={foldedActive || undefined}
                      role="button"
                      aria-label={foldedActive ? 'All view actions, some folded here are on' : 'All view actions'}
                      aria-haspopup="menu"
                      tabIndex={0}
                      className={button({ class: 'relative' })}
                      onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          event.currentTarget.click()
                        }
                      }}
                      onClick={(event: React.MouseEvent) => {
                        setMenuState('viewConfig', {
                          open: true,
                          top: `${event.currentTarget.getBoundingClientRect().bottom + 10}px`,
                          left: `${event.currentTarget.getBoundingClientRect().right - viewConfigWidth}px`,
                          right: 'auto',
                          bottom: 'auto'
                        })
                      }}
                    >
                      <ViewActions />
                      {foldedActive && (
                        <span aria-hidden className="absolute top-0.5 right-0.5 size-1.5 rounded-full bg-blue" />
                      )}
                    </div>
                  </Tooltip>
                </ToolbarGroup>
              </div>
            </div>
          </div>

          {menusAndModals}
        </div>
      )
    }

    return (
      <div
        className={`flex w-full shrink-0 items-center box-border select-none border-b border-divider overflow-x-auto overflow-y-hidden ${TOOLBAR_HEIGHT_CLASS} ${sidebarWidth === 0 && !inProcess ? 'py-[5px] pr-[5px] pl-[50px]' : 'p-[5px]'}`}
        ref={toolbarRowRef}
        data-testid="spreadsheet-toolbar"
        onMouseDown={resetSpreadsheetClick}
      >
        {tableName}
        <div className="border-r-2 border-divider h-[80%]" />
        {viewPicker}
        <div className="flex">
          {viewConfigButton}
          {chartButton}
          {pivotButton}
          {matrixButton}
          {colorButton}
          {filterButton}
          {sortButton}
          {groupButton}
          {hideButton}
          {importButton}
          {exportButton}
          {joinsButton}
          {historyButton}
          {lockButton}
          {requiredButton}
          {columnOrderButton}
          {freezeButton}
          {rowHeightButton}
          {linkButton}
          {permissionsButton}
        </div>

        {/* Sticky buttons - always visible on the right */}
        <div className="flex items-center shrink-0 pl-1 bg-surface">
          {saveButton}
          {searchButton}
          {expandButton}
          {collapseButton}
          {settingsButton}
        </div>

        {menusAndModals}
      </div>
    )
  } else {
    return <ToolbarSkeleton inProcess={!!inProcess} />
  }
}

export default Toolbar
